import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  FiArrowLeft,
  FiArrowRight,
  FiCheckCircle,
  FiLoader,
  FiMaximize2,
  FiMinimize2,
} from 'react-icons/fi';

import Question from './Question';
import {
  selectExamState,
  setQuestionIndex,
  submitAnswer,
  nextQuestion,
  previousQuestion,
  submitExamSession,
} from '../../redux/examSlice';
import { selectAuthUserId } from '../../redux/authSlice';
import LoadingSpinner from '../ui/LoadingSpinner';
import './Exam.css';

const Exam = () => {
  const dispatch = useDispatch();
  const state = useSelector(selectExamState);
  const userId = useSelector(selectAuthUserId);
  const [isExpanded, setIsExpanded] = useState(false);
  const { questions, studentAnswers, currentQuestionIndex, exam, loading, error, result } = state;

  const currentQuestion = useMemo(
    () => questions?.[currentQuestionIndex] || {},
    [questions, currentQuestionIndex]
  );

  if (!currentQuestion?.question_id) {
    if (loading) return <LoadingSpinner message="Loading exam questions..." />;
    if (error) return <p style={{ color: '#b00020' }}>{error}</p>;
    return <p>Question not available.</p>;
  }

  const answeredCount = Object.keys(studentAnswers || {}).length;
  const canSubmit = answeredCount > 0 && !loading && !exam?.isExamSubmitted;
  const submitLabel = loading
    ? 'Submitting exam'
    : exam?.isExamSubmitted
      ? 'Exam submitted'
      : 'Submit exam';
  const progressPct =
    questions.length > 0 ? Math.round(((currentQuestionIndex + 1) / questions.length) * 100) : 0;

  return (
    <div className={`exam-question ${isExpanded ? 'exam-question--expanded' : ''}`}>
      <div className="exam-progress-strip">
        <div className="exam-progress-track" aria-label="Question progress">
          <div className="exam-progress-fill" style={{ width: `${progressPct}%` }} />
        </div>
        <div className="exam-question-rail">
          {questions.map((question, index) => {
            const qid = question.question_id;
            const isActive = index === currentQuestionIndex;
            const isAnswered = studentAnswers[qid] !== undefined && studentAnswers[qid] !== '';
            return (
              <button
                key={qid || `q_${index + 1}`}
                type="button"
                className={`exam-question-pill ${isActive ? 'is-active' : ''} ${isAnswered ? 'is-answered' : ''}`}
                onClick={() => dispatch(setQuestionIndex(index))}
                aria-label={`Go to question ${index + 1}`}
                title={`Question ${index + 1}`}
              >
                {index + 1}
              </button>
            );
          })}
        </div>
      </div>

      <div className="question-header">
        <h3>
          Question {currentQuestionIndex + 1} of {questions.length}
        </h3>
        <div className="question-metadata">
          <span className="difficulty">{currentQuestion.difficulty_level}</span>
          <span className="points">{currentQuestion.points} points</span>
          <button
            type="button"
            className="exam-nav-icon-button"
            onClick={() => setIsExpanded((value) => !value)}
            aria-label={isExpanded ? 'Collapse question area' : 'Expand question area'}
            title={isExpanded ? 'Collapse question area' : 'Expand question area'}
          >
            {isExpanded ? <FiMinimize2 /> : <FiMaximize2 />}
          </button>
        </div>
      </div>

      <div className="question-content">
        <p className="question-text">{currentQuestion.question_text}</p>

        <Question
          key={currentQuestion.question_id}
          question={currentQuestion.question_text}
          questionId={currentQuestion.question_id}
          fieldType={currentQuestion.field_type}
          questionType={currentQuestion.question_type}
          options={currentQuestion.options || []}
          ui={currentQuestion.ui || {}}
          studentAnswer={studentAnswers[currentQuestion.question_id] || ''}
          onAnswerChange={(questionId, answer) => dispatch(submitAnswer({ questionId, answer }))}
        />
      </div>

      <div className="navigation-controls">
        <button
          className="exam-nav-icon-button"
          onClick={() => dispatch(previousQuestion())}
          disabled={currentQuestionIndex === 0}
          aria-label="Previous question"
          title="Previous question"
        >
          <FiArrowLeft />
        </button>

        <span className="question-counter">
          {currentQuestionIndex + 1} / {questions.length}
        </span>

        <button
          className="exam-nav-icon-button"
          onClick={() => dispatch(nextQuestion())}
          disabled={currentQuestionIndex >= questions.length - 1}
          aria-label="Next question"
          title="Next question"
        >
          <FiArrowRight />
        </button>
      </div>

      <div className="navigation-controls" style={{ marginTop: 12 }}>
        <button
          className="exam-submit-button"
          onClick={() => dispatch(submitExamSession({ userId }))}
          disabled={!canSubmit}
          aria-label={submitLabel}
          title={submitLabel}
        >
          {loading ? <FiLoader className="exam-icon-spin" /> : null}
          {!loading && exam?.isExamSubmitted ? <FiCheckCircle /> : null}
          {!loading && !exam?.isExamSubmitted ? 'SUBMIT' : null}
        </button>
      </div>

      {error ? <p style={{ color: '#b00020', marginTop: 8 }}>{error}</p> : null}

      {result ? (
        <div className="exam-result-panel">
          <strong>
            Score: {result.scoreRaw}/{result.scoreMax} ({result.scorePct}%)
          </strong>
          {result.grade ? <div className="exam-result-grade">Grade: {result.grade}</div> : null}
        </div>
      ) : null}
    </div>
  );
};

export default Exam;
